import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import StepIndicator from "../components/steps/StepIndicator";
import PromptStep from "../components/steps/PromptStep";
import TopicStep from "../components/steps/TopicStep";
import FileStep from "../components/steps/DriveStep";
import YoutubeStep from "../components/steps/YoutubeStep";
import QuestionDistributionForm from "../components/forms/QuestionDistributionForm";
import AssignmentOverviewForm from "../components/forms/AssignmentOverviewForm";
import LearningOutcomeStep from "../components/steps/LearningOutcomeStep";
import {
  createHomeworkAssignment,
  createQuizAssignment,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import {
  CreateHomeworkAssignmentRequestBody,
  CreateQuizAssignmentRequestBody,
} from "@packages/api/madlenAPI.schemas";
import { getEnvironment } from "@packages/utils";
import { Button, Text } from "../../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";

const AssignmentGeneratorView = () => {
  const { type } = useParams<{ type: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [assignmentData, setAssignmentData] = useState<
    (CreateHomeworkAssignmentRequestBody | CreateQuizAssignmentRequestBody) & {
      hwAssignmentType: "HOMEWORK" | "QUIZ";
      cefrLevel?: import("@packages/api/madlenAPI.schemas").CreatePodcastRequestDtoCefrLevel;
    }
  >({
    assignmentDifficulty: "MEDIUM",
    courseType:
      i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN",
    description: "",
    driveFileIds: [],
    duration: 30,
    endDate: new Date(
      new Date().getTime() + 2 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    environment: getEnvironment(),
    generator: "PROMPT",
    grade: 8,
    hwAssignmentType: "HOMEWORK",
    contentLanguage: i18n.language as "tr" | "en" | "de" | "fr",
    name: "",
    prompt: "",
    questionStyle: "",
    questionTypes: [
      {
        questionType: "FB",
        count: 3, // Default to 3
        point: 2,
        difficulty: "MEDIUM",
      },
      {
        questionType: "OE",
        count: 3, // Default to 3
        point: 10,
        difficulty: "MEDIUM",
      },
      {
        questionType: "TF",
        count: 3, // Default to 3
        point: 1,
        difficulty: "MEDIUM",
      },
      {
        questionType: "TEST",
        count: 3, // Default to 3
        point: 5,
        difficulty: "MEDIUM",
      },
    ],
    topicIds: [],
    learningOutcomeIds: [],
    maarifLearningOutcomeIds: [],
    youtubeLink: "",
  });

  const steps = [
    {
      title: t("assignments.generator.steps.properties"),
      description: t("assignments.generator.steps.propertiesDesc"),
    },
    {
      title: t("assignments.generator.steps.overview"),
      description: t("assignments.generator.steps.assignDesc"),
    },
  ];

  const handleUpdateAssignmentData = (
    updates: Partial<
      CreateHomeworkAssignmentRequestBody & {
        cefrLevel?: import("@packages/api/madlenAPI.schemas").CreatePodcastRequestDtoCefrLevel;
      }
    >,
  ) => {
    // No need to enforce minimum count here, allow 0
    setAssignmentData((prev) => ({ ...prev, ...updates }));
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        switch (type) {
          case "prompt":
            return (
              <>
                <PromptStep
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
                <QuestionDistributionForm
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
              </>
            );
          case "topic_list":
            return (
              <>
                <TopicStep
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
                <QuestionDistributionForm
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
              </>
            );
          case "drive_file":
            return (
              <>
                <FileStep
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
                <QuestionDistributionForm
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
              </>
            );
          case "youtube_link":
            return (
              <>
                <YoutubeStep
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
                <QuestionDistributionForm
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
              </>
            );
          case "learning_outcome":
            return (
              <>
                <LearningOutcomeStep
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
                <QuestionDistributionForm
                  assignmentData={assignmentData}
                  onChange={handleUpdateAssignmentData}
                />
              </>
            );
          default:
            return <div>Invalid generation type</div>;
        }
      case 2:
        return <AssignmentOverviewForm assignmentData={assignmentData} />;
      default:
        return null;
    }
  };

  const validatePromptGeneration = () => {
    if (!assignmentData.prompt || assignmentData.prompt.trim() === "") {
      addToast({
        title: t("notifications.assignment.emptyPrompt"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateTopicGeneration = () => {
    if (!assignmentData.topicIds?.length) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateDriveGeneration = () => {
    if (
      !assignmentData.driveFileIds?.length &&
      !assignmentData.teacherDocumentIds?.length
    ) {
      addToast({
        title: t("notifications.assignment.emptyFiles"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateYoutubeGeneration = () => {
    if (!assignmentData.youtubeLink?.trim()) {
      addToast({
        title: t("notifications.assignment.emptyYoutubeLink"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateLearningOutcomeGeneration = () => {
    if (
      !assignmentData.learningOutcomeIds?.length &&
      !assignmentData.maarifLearningOutcomeIds?.length
    ) {
      addToast({
        title: t("notifications.assignment.emptyLearningOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  // Updated validation to check if total question count is 0
  const validateQuestionCounts = () => {
    const totalQuestionCount = assignmentData.questionTypes.reduce(
      (total, qType) => total + (qType.count ?? 0),
      0,
    );

    if (totalQuestionCount === 0) {
      addToast({
        title: t("notifications.assignment.zeroQuestions"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const getValidationFunction = () => {
    switch (type) {
      case "prompt":
        return validatePromptGeneration;
      case "topic_list":
        return validateTopicGeneration;
      case "drive_file":
        return validateDriveGeneration;
      case "youtube_link":
        return validateYoutubeGeneration;
      case "learning_outcome":
        return validateLearningOutcomeGeneration;
      default:
        return () => false;
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleNext = async () => {
    if (step === 1) {
      const isQuiz = assignmentData.hwAssignmentType === "QUIZ";
      const isDurationExceeded =
        ((assignmentData as CreateQuizAssignmentRequestBody)?.duration ?? 0) >
        1440;
      if (isQuiz && isDurationExceeded) {
        addToast({
          title: t("notifications.assignment.invalidDuration"),
          variant: "error",
          duration: 3000,
        });
        return;
      }

      // Validate total question counts first
      if (!validateQuestionCounts()) {
        return;
      }

      // Then validate the specific generation type
      const validationFunction = getValidationFunction();
      const isValid = validationFunction();

      if (!isValid) return;
    }

    if (step < steps.length) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        // Remove question types with 0 count before sending to API
        const filteredQuestionTypes = assignmentData.questionTypes.filter(
          (qType) => (qType.count ?? 0) > 0,
        );

        //Prompt is being trimmed before sending
        const body: (
          | CreateHomeworkAssignmentRequestBody
          | CreateQuizAssignmentRequestBody
        ) & {
          hwAssignmentType: "HOMEWORK" | "QUIZ";
          cefrLevel?: import("@packages/api/madlenAPI.schemas").CreatePodcastRequestDtoCefrLevel;
        } = JSON.parse(
          JSON.stringify({
            ...assignmentData,
            questionTypes: filteredQuestionTypes,
            prompt: assignmentData.prompt?.trim(),
            endDate:
              assignmentData.hwAssignmentType === "HOMEWORK"
                ? (assignmentData as CreateHomeworkAssignmentRequestBody)
                    .endDate
                : null,
            duration:
              assignmentData.hwAssignmentType === "QUIZ"
                ? (assignmentData as CreateQuizAssignmentRequestBody).duration
                : null,
            cefrLevel: assignmentData.cefrLevel,
          }),
        );

        if (body.topicIds && body.topicIds?.length > 0) {
          body.generator = "TOPIC_LIST";
        } else if (
          (body.learningOutcomeIds && body.learningOutcomeIds?.length > 0) ||
          (body.maarifLearningOutcomeIds &&
            body.maarifLearningOutcomeIds?.length > 0)
        ) {
          const savedValue = localStorage.getItem("isMaarifEnabled");
          const isMaarif = savedValue ? JSON.parse(savedValue) : false;
          body.generator =
            isMaarif || assignmentData.grade < 5
              ? "MAARIF_LEARNING_OUTCOME"
              : "LEARNING_OUTCOME";
        } else if (body.youtubeLink) {
          body.generator = "YOUTUBE_LINK";
        } else if (body.driveFileIds && body.driveFileIds?.length > 0) {
          body.generator = "DRIVE_FILE";
        } else if (
          body.teacherDocumentIds &&
          body.teacherDocumentIds?.length > 0
        ) {
          body.generator = "TEACHER_DOCUMENT";
        } else {
          body.generator = "PROMPT";
        }
        let newAssignment;
        if (body.hwAssignmentType === "QUIZ") {
          newAssignment = await createQuizAssignment(body);
        } else {
          newAssignment = await createHomeworkAssignment(body);
        }
        console.log("newAssignment", newAssignment);
        track(
          {
            category: "ContentGeneration",
            action: "single.assignment.generate",
            label: "Single assingment generation click",
            nonInteraction: false,
          },
          {
            location: "/single-assignment/generate",
            element: "generate-button",

            singleAssignmentCourseType: assignmentData.courseType,
            singleAssignmentDifficulty: assignmentData.assignmentDifficulty,
            singleAssignmentDuration:
              assignmentData.hwAssignmentType === "QUIZ"
                ? (assignmentData as CreateQuizAssignmentRequestBody).duration
                : undefined,
            singleAssignmentEndDate:
              assignmentData.hwAssignmentType === "HOMEWORK"
                ? (assignmentData as CreateHomeworkAssignmentRequestBody)
                    .endDate
                : null,
            singleAssignmentGeneratorType: assignmentData.generator,
            singleAssignmentPrompt: assignmentData.prompt,
          },
        );
        addToast({
          title: t("notifications.assignment.createSuccess"),
          variant: "success",
          duration: 3000,
        });
        setLoading(false);
        navigate(`/assignments/${newAssignment.id}/edit`);
      } catch (error: any) {
        console.log("error", error);
        if (
          error.response?.data.message ===
          "Content contains inappropriate language"
        ) {
          setLoading(false);
          addToast({
            title: t("common.inappropriateContent.title"),
            description: t("common.inappropriateContent.description"),
            variant: "error",
            duration: 3000,
          });
        } else {
          addToast({
            title: t("notifications.assignment.createError"),
            variant: "error",
            duration: 3000,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate("/assignments/generate");
    }
  };

  // Helper to get dynamic title/description keys based on type
  const getTitleKey = () => {
    switch (type) {
      case "prompt":
        return "assignments.generator.prompt.title";
      case "topic_list":
        return "assignments.generator.topic.title";
      case "drive_file":
        return "assignments.generator.file.title";
      case "youtube_link":
        return "assignments.generator.youtube.title";
      case "learning_outcome":
        return "assignments.generator.learning_outcome.title";
      case "maarif_learning_outcome":
        return "assignments.generator.maarif_learning_outcome.title";
      default:
        return "assignments.generator.title";
    }
  };

  const getDescriptionKey = () => {
    switch (type) {
      case "prompt":
        return "assignments.generator.prompt.description";
      case "topic_list":
        return "assignments.generator.topic.description";
      case "drive_file":
        return "assignments.generator.file.description";
      case "youtube_link":
        return "assignments.generator.youtube.description";
      case "learning_outcome":
        return "assignments.generator.learning_outcome.description";
      case "maarif_learning_outcome":
        return "assignments.generator.maarif_learning_outcome.description";
      default:
        return "assignments.generator.description";
    }
  };

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Sayfa başlığı, açıklama ve step indicator */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t(getTitleKey())}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t(getDescriptionKey())}
            </Text>
          </div>
          <div className="flex-shrink-0 w-full lg:w-auto">
            <StepIndicator currentStep={step} steps={steps} />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-3 sm:p-4 lg:p-6 mt-3 sm:mt-4 lg:mt-6">
          <Text
            variant="h2"
            className="mb-3 sm:mb-4 text-base sm:text-lg lg:text-xl xl:text-2xl break-words"
          >
            {steps[step - 1].title}
          </Text>

          <div className="min-w-0">{renderStepContent()}</div>
        </div>

        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
          <Button
            onClick={handleBack}
            size="md"
            rounded="full"
            bgColor="bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
            textColor="text-gray-700 dark:text-gray-200"
            variant="outline"
            className="border-gray-300 dark:border-gray-600 w-full sm:w-auto min-w-0"
          >
            <span className="truncate">{t("common.back")}</span>
          </Button>
          <Button
            onClick={handleNext}
            size="sm"
            rounded="full"
            bgColor="bg-primary-500 dark:bg-primary-500 hover:bg-indigo-700"
            textColor="text-white"
            className={`w-full sm:w-auto px-4 sm:px-6 lg:px-12 min-w-0 ${loading ? "opacity-50 cursor-not-allowed hover:bg-indigo-600" : ""}`}
            disabled={loading}
            data-testid="create-assignment-from-prompt-next-button"
          >
            <span className="truncate">
              {step === steps.length ? t("common.generate") : t("common.next")}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentGeneratorView;
